import React, { useState } from 'react';
import { Select, Tag, Tooltip } from 'antd';
import CreateVehicleModal from './CreateVehicleModal';
import { vehicleCategoryLabel } from '../../constants/vehicleCategory';
import type { Vehicle } from '../../store/api/referencesApi';
import type { VehicleCategoryCode } from '../../types';

const fieldLabelClass =
  'block text-sm font-medium text-gray-700 mb-2';

function VehicleOptionLabel({ v }: { v: Vehicle }) {
  const cat = vehicleCategoryLabel(v.vehicleCategory);
  const isTrailer = v.vehicleCategory === 'TRAILER';
  return (
    <span className="inline-flex w-full min-w-0 items-center gap-3">
      <span className="truncate font-medium text-gray-900">{v.registrationNumber}</span>
      <span className="h-3 w-px shrink-0 bg-gray-300" aria-hidden />
      <Tag color={isTrailer ? 'orange' : 'blue'} className="!m-0 shrink-0">
        {cat}
      </Tag>
    </span>
  );
}

const selectInGroupClassName =
  'min-w-0 flex-1 w-full [&_.ant-select-selector]:!border-0 [&_.ant-select-selector]:!bg-transparent [&_.ant-select-selector]:!shadow-none [&_.ant-select-selector]:!h-10 [&_.ant-select-selector]:!min-h-[40px] [&_.ant-select-selector]:!py-0 [&_.ant-select-selector]:!pl-3 [&_.ant-select-selector]:!pr-10';

const selectTrailerInGroupClassName =
  'min-w-0 flex-1 w-full [&_.ant-select-selector]:!border-0 [&_.ant-select-selector]:!bg-transparent [&_.ant-select-selector]:!shadow-none [&_.ant-select-selector]:!h-10 [&_.ant-select-selector]:!min-h-[40px] [&_.ant-select-selector]:!py-0 [&_.ant-select-selector]:!pl-3 [&_.ant-select-selector]:!pr-12';

export interface VehicleSelectInlineProps {
  /** Список ТС (уже отфильтрованный по категории). */
  vehicles: Vehicle[];
  placeholder: string;
  allowClear: boolean;
  /** Категория для модалки «добавить ТС». */
  modalCategory: VehicleCategoryCode;
  addButtonTooltip: string;
  addButtonAriaLabel: string;
  /** RTK `refetch` и др. — оборачиваем внутри через `Promise.resolve`. */
  onRefetchVehicles: () => unknown;
  /** После создания ТС (после refetch). По умолчанию — только onChange(id). */
  onVehicleCreated?: (vehicle: Vehicle) => void;
  /** Дополнительно после смены значения (синхронизация stepData и т.п.). */
  onCommittedChange?: (id: number | undefined) => void;
  /** Для Ant Design Form */
  value?: number;
  onChange?: (id: number | undefined) => void;
  /** Подпись над полем; если не задана — только контрол (удобно с Form.Item label). */
  label?: string;
  hint?: string;
  error?: boolean;
  errorMessage?: string;
  getPopupContainer?: (trigger: HTMLElement) => HTMLElement;
}

const defaultPopup = (trigger: HTMLElement): HTMLElement => {
  const el =
    trigger.closest('.ant-modal-root') ??
    trigger.closest('.fixed') ??
    document.body;
  return el instanceof HTMLElement ? el : document.body;
};

/**
 * Селект ТС + кнопка «+» и модалка создания — тот же паттерн, что в форме водителя.
 */
const VehicleSelectInline: React.FC<VehicleSelectInlineProps> = ({
  vehicles,
  placeholder,
  allowClear,
  modalCategory,
  addButtonTooltip,
  addButtonAriaLabel,
  onRefetchVehicles,
  onVehicleCreated,
  onCommittedChange,
  value,
  onChange,
  label,
  hint,
  error,
  errorMessage,
  getPopupContainer = defaultPopup
}) => {
  const [vehicleModalOpen, setVehicleModalOpen] = useState(false);

  const selectClass = allowClear ? selectTrailerInGroupClassName : selectInGroupClassName;

  return (
    <div>
      {label ? <label className={fieldLabelClass}>{label}</label> : null}
      {hint ? <p className="text-xs text-gray-500 mb-2">{hint}</p> : null}
      <div
        className={`flex overflow-hidden rounded-[6px] border bg-[#fafafa] ${
          error ? 'border-red-500' : 'border-[#dcdcdc]'
        }`}
      >
        <Select
          className={selectClass}
          variant="borderless"
          placeholder={placeholder}
          allowClear={allowClear}
          value={value}
          onChange={(val) => {
            const id = val != null ? Number(val) : undefined;
            onChange?.(id);
            onCommittedChange?.(id);
          }}
          options={vehicles.map((v) => ({
            value: v.id,
            label: <VehicleOptionLabel v={v} />
          }))}
          getPopupContainer={(trigger) =>
            getPopupContainer(trigger instanceof HTMLElement ? trigger : (trigger as unknown as HTMLElement))
          }
          popupMatchSelectWidth={false}
        />
        <Tooltip title={addButtonTooltip}>
          <button
            type="button"
            onClick={() => setVehicleModalOpen(true)}
            className="flex h-10 w-10 shrink-0 items-center justify-center border-0 border-l border-l-[#dcdcdc] bg-[#f5f5f5] p-0 text-lg leading-none hover:bg-[#eeeeee]"
            aria-label={addButtonAriaLabel}
          >
            +
          </button>
        </Tooltip>
      </div>
      {error && errorMessage ? <p className="text-red-500 text-sm mt-1">{errorMessage}</p> : null}

      <CreateVehicleModal
        isOpen={vehicleModalOpen}
        onClose={() => setVehicleModalOpen(false)}
        initialVehicleCategory={modalCategory}
        onVehicleCreated={async (v: Vehicle) => {
          await Promise.resolve(onRefetchVehicles());
          onVehicleCreated?.(v);
          onChange?.(v.id);
          onCommittedChange?.(v.id);
          setVehicleModalOpen(false);
        }}
      />
    </div>
  );
};

export default VehicleSelectInline;
